import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Stack, Typography } from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import ReadinessService from '../../../shared/services/readiness/readiness.service'
import { IReadinessFinding } from '../../../shared/services/readiness/readiness.model'
import { ReportPeriodContext } from '../../../context/report-periods/report-periods'
import { UserDetailsContext } from '../../../context/user-details/user-details'

// A location or group page shows its own readiness findings inline: fixing where the problem
// is beats a round trip through a list. Renders nothing while loading, for entities with no
// open findings (acknowledged ones are already disclosed), or for roles without the org-wide
// readiness view.
//
// The banner stays calm (blue, informational) while the reporting period is still running,
// and only escalates to warning/red once the period's end date has passed with findings
// still open and unacknowledged.
export const EntityFindingsPanel: FunctionComponent<{
  entityType: 'location' | 'group',
  entityId: string,
}> = ({ entityType, entityId }) => {

  const navigate = useNavigate()
  const readinessService = useMemo(() => new ReadinessService(), [])
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const roles = userDetailsContext.userDetails?.roles || []
  const canSeeReadiness = roles.includes('admin') || roles.includes('analyst')
  const reportPeriodId = reportPeriodContext.lastSelectedReportId

  const [findings, setFindings] = useState<IReadinessFinding[]>([])

  useEffect(() => {
    if (!canSeeReadiness || !reportPeriodId || !entityId) return
    readinessService.getForPeriod(reportPeriodId)
      .then(response => {
        setFindings(response.findings.filter(f => f.entity?.type === entityType && f.entity?.id === entityId))
      })
      .catch(() => setFindings([]))
  }, [canSeeReadiness, reportPeriodId, entityType, entityId, readinessService])

  const periodEndDate = reportPeriodContext.allReportPeriods.find(p => p.id === reportPeriodId)?.endDate
  const periodHasPassed = !!periodEndDate && new Date(periodEndDate) < new Date()

  if (findings.length === 0) return null

  return (
    <Alert
      severity={periodHasPassed ? 'warning' : 'info'}
      variant={periodHasPassed ? 'outlined' : 'standard'}
      icon={periodHasPassed ? <InfoOutlined sx={{ color: 'text.primary' }} /> : undefined}
      // mt 1: both pages place this between a tab-spaced heading (mb 1) and the tabs, so it
      // tops the heading's gap up to the standard 16px.
      sx={{ mt: 1, mb: 2, ...(periodHasPassed ? { p: 2, borderRadius: '10px', color: 'text.primary', borderColor: 'divider', '& .MuiAlert-message': { py: 0 }, '& .MuiAlert-action': { color: 'text.primary', alignItems: 'center', pt: 0 } } : {}) }}
      action={
        <Button color='inherit' size='small' onClick={() => navigate('/readiness')}>
          <Typography variant='body2'>View readiness</Typography>
        </Button>
      }
    >
      <Stack spacing={0.25}>
        {findings.slice(0, 3).map(finding => (
          <Typography key={`${finding.checkId}|${finding.entityKey}`} variant='body2'>{finding.title}</Typography>
        ))}
        {findings.length > 3 &&
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>and {findings.length - 3} more</Typography>}
      </Stack>
    </Alert>
  )
}
